import { create } from 'zustand';
import { templatesApi } from '../api/templates';
import type { Template, CreateTemplateRequest, ApplyTemplateRequest } from '@affogato/shared';

interface TemplateState {
  templates: Template[];
  selectedTemplate: Template | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTemplates: () => Promise<void>;
  selectTemplate: (template: Template | null) => void;
  createTemplate: (data: CreateTemplateRequest) => Promise<Template>;
  updateTemplate: (id: string, data: Partial<CreateTemplateRequest>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  applyTemplate: (id: string, data: ApplyTemplateRequest) => Promise<{ foldersCreated: number }>;
  clearError: () => void;
}

export const useTemplateStore = create<TemplateState>((set) => ({
  templates: [],
  selectedTemplate: null,
  isLoading: false,
  error: null,

  fetchTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.getAll();
      set({ templates: response.templates, isLoading: false });
    } catch (error) {
      set({ error: 'Error al cargar plantillas', isLoading: false });
    }
  },

  selectTemplate: (template) => {
    set({ selectedTemplate: template });
  },

  createTemplate: async (data: CreateTemplateRequest) => {
    set({ isLoading: true, error: null });
    try {
      const template = await templatesApi.create(data);
      set((state) => ({
        templates: [...state.templates, template],
        isLoading: false,
      }));
      return template;
    } catch (error) {
      set({ error: 'Error al crear plantilla', isLoading: false });
      throw error;
    }
  },

  updateTemplate: async (id: string, data: Partial<CreateTemplateRequest>) => {
    set({ isLoading: true, error: null });
    try {
      await templatesApi.update(id, data);
      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, ...data, updatedAt: new Date().toISOString() } : t
        ),
        isLoading: false,
      }));
    } catch (error) {
      set({ error: 'Error al actualizar plantilla', isLoading: false });
      throw error;
    }
  },

  deleteTemplate: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await templatesApi.delete(id);
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        selectedTemplate: state.selectedTemplate?.id === id ? null : state.selectedTemplate,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: 'Error al eliminar plantilla', isLoading: false });
      throw error;
    }
  },

  applyTemplate: async (id: string, data: ApplyTemplateRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.apply(id, data);
      set({ isLoading: false });
      return { foldersCreated: response.foldersCreated };
    } catch (error) {
      set({ error: 'Error al aplicar plantilla', isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
